import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../stores/authStore';
import { RouteSkeleton } from './RouteSkeleton';
import { ForbiddenState } from './ForbiddenState';
import { FORBIDDEN_REASONS } from '../lib/constants/forbiddenReasons';
import { LOGIN_ROUTE } from '../lib/constants/authRoutes';
import { useShallow } from 'zustand/react/shallow';

interface PermissionGuardProps {
  requiredPermission: string;
}

/**
 * Route-level permission guard. Shows the forbidden screen (never a silent redirect) when the
 * current user lacks the required PermissionKey. TenantAdmin is implicit-all (handled inside
 * usePermissions).
 */
export const PermissionGuard = ({ requiredPermission }: PermissionGuardProps) => {
  const { isAuthenticated, user, sessionRestoring } = useAuthStore(useShallow((s) => ({ isAuthenticated: s.isAuthenticated, user: s.user, sessionRestoring: s.sessionRestoring })));
  const { has } = usePermissions();
  const location = useLocation();

  if (sessionRestoring) return <RouteSkeleton />;

  if (!isAuthenticated) {
    // Same returnTo hop as RoleGuard (AB#1864); LoginPage re-validates it with sanitizeReturnTo.
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`${LOGIN_ROUTE}?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  if (!user) return <RouteSkeleton />;
  if (!has(requiredPermission)) return <ForbiddenState reason={FORBIDDEN_REASONS.permission} />;

  return <Outlet />;
};

export default PermissionGuard;
